import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IoLanguage } from 'react-icons/io5';
import axios from 'axios';

interface Currency {
    code: string;
    symbol: string;
    name: string;
}

const languageGroups = [
    {
        region: 'Primary Languages',
        languages: [
            { code: 'en', name: 'English', flag: '🇺🇸' },
            { code: 'sn', name: 'Shona', flag: '🇿🇼' },
            { code: 'sw', name: 'Swahili', flag: '🇰🇪' },
            { code: 'fr', name: 'Français', flag: '🇫🇷' }
        ]
    },
    {
        region: 'African Languages',
        languages: [
            { code: 'af', name: 'Afrikaans', flag: '🇿🇦' },
            { code: 'am', name: 'Amharic', flag: '🇪🇹' },
            { code: 'bm', name: 'Bambara', flag: '🇲🇱' },
            { code: 'ny', name: 'Chichewa', flag: '🇲🇼' },
            { code: 'ee', name: 'Ewe', flag: '🇬🇭' },
            { code: 'ha', name: 'Hausa', flag: '🇳🇬' },
            { code: 'ig', name: 'Igbo', flag: '🇳🇬' },
            { code: 'rw', name: 'Kinyarwanda', flag: '🇷🇼' },
            { code: 'ln', name: 'Lingala', flag: '🇨🇩' },
            { code: 'lg', name: 'Luganda', flag: '🇺🇬' },
            { code: 'mg', name: 'Malagasy', flag: '🇲🇬' },
            { code: 'om', name: 'Oromo', flag: '🇪🇹' },
            { code: 'st', name: 'Sesotho', flag: '🇱🇸' },
            { code: 'sn', name: 'Shona', flag: '🇿🇼' },
            { code: 'so', name: 'Somali', flag: '🇸🇴' },
            { code: 'sw', name: 'Swahili', flag: '🇰🇪' },
            { code: 'ti', name: 'Tigrinya', flag: '🇪🇷' },
            { code: 'tw', name: 'Twi', flag: '🇬🇭' },
            { code: 'xh', name: 'Xhosa', flag: '🇿🇦' },
            { code: 'yo', name: 'Yoruba', flag: '🇳🇬' },
            { code: 'zu', name: 'Zulu', flag: '🇿🇦' }
        ]
    },
    {
        region: 'Global Languages',
        languages: [
            { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
            { code: 'bn', name: 'Bengali', flag: '🇧🇩' },
            { code: 'zh-CN', name: 'Chinese (Simplified)', flag: '🇨🇳' },
            { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
            { code: 'es', name: 'Spanish', flag: '🇪🇸' },
            { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
            { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
            { code: 'ru', name: 'Russian', flag: '🇷🇺' },
            { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
            { code: 'de', name: 'German', flag: '🇩🇪' },
            { code: 'ko', name: 'Korean', flag: '🇰🇷' },
            { code: 'it', name: 'Italian', flag: '🇮🇹' },
            { code: 'tr', name: 'Turkish', flag: '🇹🇷' }
        ]
    }
];

export const LanguageCurrencyPicker: React.FC = () => {
    const { i18n } = useTranslation();
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [selectedCurrency, setSelectedCurrency] = useState(localStorage.getItem('user-currency') || 'USD');
    const [isOpen, setIsOpen] = useState(false);
    const [currentLangCode, setCurrentLangCode] = useState(i18n.language.split('-')[0]);
    const popupRef = useRef<HTMLDivElement>(null);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        // Detect google translate cookie
        const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
        if (match && match[1]) {
            setCurrentLangCode(match[1]);
        }
    }, [i18n.language]);

    useEffect(() => {
        const fetchCurrencies = async () => {
            try {
                const response = await axios.get('/api/financial/currencies');
                if (response.data.success) {
                    setCurrencies(response.data.currencies);
                }
            } catch (error) {
                // Fallback basic list if API fails
                setCurrencies([
                    { code: 'USD', symbol: '$', name: 'US Dollar' },
                    { code: 'ZiG', symbol: 'ZiG', name: 'Zimbabwe Gold' },
                    { code: 'KES', symbol: 'KSh', name: 'Kenya Shilling' },
                    { code: 'ZAR', symbol: 'R', name: 'South Africa Rand' }
                ]);
            }
        };
        fetchCurrencies();
    }, []);

    const handleLanguageChange = (code: string) => {
        const coreLanguages = ['en'];
        
        // Always reset current language so ui updates immediately
        setCurrentLangCode(code);
        
        if (coreLanguages.includes(code)) {
            // Unset Google Translate cookie
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            if (window.location.hostname !== 'localhost') {
                const domainParts = window.location.hostname.split('.');
                const rootDomain = domainParts.length > 2 ? `.${domainParts.slice(-2).join('.')}` : `.${window.location.hostname}`;
                document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain}`;
            }
            // Set i18n
            i18n.changeLanguage(code);
            window.location.reload();
        } else {
            // Ensure native text is english so Google translates from english accurately
            i18n.changeLanguage('en');
            // Set Google Translate cookie
            document.cookie = `googtrans=/en/${code}; path=/;`;
            if (window.location.hostname !== 'localhost') {
              const domainParts = window.location.hostname.split('.');
              const rootDomain = domainParts.length > 2 ? `.${domainParts.slice(-2).join('.')}` : `.${window.location.hostname}`;
              document.cookie = `googtrans=/en/${code}; path=/; domain=${rootDomain}`;
            }
            window.location.reload();
        }
        setIsOpen(false);
    };

    const handleCurrencyChange = (code: string) => {
        setSelectedCurrency(code);
        localStorage.setItem('user-currency', code);
        window.dispatchEvent(new Event('currencyChange'));
        setIsOpen(false);
    };

    return (
        <div className="relative z-[100]" ref={popupRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-all text-sm"
            >
                <IoLanguage className="text-lg text-blue-400" />
                <span className="uppercase">{currentLangCode}</span>
                <span className="w-px h-3 bg-white/20 mx-1" />
                <span className="text-emerald-400">{selectedCurrency}</span>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-[340px] sm:w-[400px] bg-[#0a0f18] border border-white/10 rounded-xl shadow-2xl p-4 z-[100] backdrop-blur-xl">
                    <div className="mb-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Display Language</p>
                        <div className="max-h-64 overflow-y-auto pr-1 custom-scrollbar space-y-4">
                            {languageGroups.map((group) => (
                                <div key={group.region}>
                                    <h4 className="text-[10px] text-white/40 uppercase tracking-wider mb-2 px-1">{group.region}</h4>
                                    <div className="grid grid-cols-2 gap-2">
                                        {group.languages.map((lng, i) => (
                                            <button
                                                key={`${group.region}-${lng.code}-${i}`}
                                                onClick={() => handleLanguageChange(lng.code)}
                                                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                                                    currentLangCode === lng.code 
                                                    ? 'bg-blue-500/20 border border-blue-500/50 text-white' 
                                                    : 'bg-white/5 border border-transparent hover:bg-white/10 text-gray-400'
                                                }`}
                                            >
                                                <span>{lng.flag}</span>
                                                <span className="truncate">{lng.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pt-3 border-t border-white/10">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Preferred Currency</p>
                        <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto pr-1 custom-scrollbar">
                            {currencies.map((curr) => (
                                <button
                                    key={curr.code}
                                    onClick={() => handleCurrencyChange(curr.code)}
                                    className={`flex flex-col items-start px-3 py-2 rounded-lg text-xs transition-all ${
                                        selectedCurrency === curr.code
                                        ? 'bg-emerald-500/20 border border-emerald-500/50 text-white'
                                        : 'bg-white/5 border border-transparent hover:bg-white/10 text-gray-400'
                                    }`}
                                >
                                    <span className="font-bold">{curr.code}</span>
                                    <span className="text-[10px] opacity-60 truncate w-full">{curr.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
